import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Shield,
  Bell,
  Mail,
  Users,
  FileText,
  Send,
  Plus,
  Edit,
  Trash2,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AdminNotifications = () => {
    const { user, apiCall, isAdmin } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [message, setMessage] = useState('');
    const [users, setUsers] = useState([]);
    const [createForm, setCreateForm] = useState({
        notification_type: 'system_announcement',
        title: '',
        message: '',
        channels: ['in_app'],
        target_users: [],
        action_url: '',
        action_text: '',
        expires_at: ''
    });
    const [sendingNotification, setSendingNotification] = useState(false);
    
    // Templates state (placeholders for future features)
    const templates = [];
    const showCreateTemplate = false;
    const editingTemplate = null;
    const templateForm = {
        name: '',
        template_type: 'system_announcement',
        subject_template: '',
        html_template: '',
        text_template: '',
        variables: [],
        is_active: true
    };

    // Queue state (placeholders for future features)
    const emailQueue = [];
    const processingQueue = false;
    
    // Bulk operations state
    const [bulkForm, setBulkForm] = useState({
        notification_type: 'system_announcement',
        title: '',
        message: '',
        channels: ['in_app'],
        target_type: 'all_users',
        target_role: 'user',
        template_id: '',
        action_url: '',
        action_text: '',
        expires_at: '',
        scheduled_at: ''
    });
    const [sendingBulk, setSendingBulk] = useState(false);

    // Redirect if not admin
    useEffect(() => {
        if (!isAdmin) {
            window.location.href = '/';
            return;
        }
    }, [isAdmin]);


    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const response = await apiCall('/admin/notifications/stats');
            
            if (response.ok) {
                const data = await response.json();
                setStats(data);
                setMessage('Dashboard loaded successfully!');
            } else {
                setMessage('Failed to load dashboard data');
            }
        } catch (err) {
            setMessage('Error loading dashboard: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const processEmailQueue = async () => {
        try {
            const response = await apiCall('/admin/notifications/process-email-queue', {
                method: 'POST'
            });

            if (response.ok) {
                const result = await response.json();
                setMessage(`✅ Processed ${result.processed_count || 0} emails successfully`);
            } else {
                setMessage('❌ Failed to process email queue');
            }
        } catch (err) {
            setMessage('❌ Error processing queue: ' + err.message);
        }
    };

    const loadUsers = async () => {
        try {
            const response = await apiCall('/admin/users');
            if (response.ok) {
                const data = await response.json();
                setUsers(data.users || []);
            }
        } catch (err) {
            console.error('Error loading users:', err);
        }
    };


    const createNotification = async () => {
        if (!createForm.title.trim() || !createForm.message.trim()) {
            setMessage('Please fill in both title and message');
            return;
        }

        setSendingNotification(true);
        setMessage('');

        try {
            const payload = {
                ...createForm,
                target_users: createForm.target_users.length > 0 ? createForm.target_users : null,
                expires_at: createForm.expires_at ? new Date(createForm.expires_at).toISOString() : null
            };

            const response = await apiCall('/admin/notifications/send-bulk', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                setMessage(`✅ Notification sent successfully! Sent to ${result.sent_count} users${result.failed_count > 0 ? `, ${result.failed_count} failed` : ''}`);
                
                // Reset form
                setCreateForm({
                    notification_type: 'system_announcement',
                    title: '',
                    message: '',
                    channels: ['in_app'],
                    target_users: [],
                    action_url: '',
                    action_text: '',
                    expires_at: ''
                });
                
                // Reload stats
                loadDashboardData();
            } else {
                const error = await response.text();
                setMessage(`❌ Failed to send notification: ${error}`);
            }
        } catch (err) {
            setMessage(`❌ Error sending notification: ${err.message}`);
        } finally {
            setSendingNotification(false);
        }
    };

    const updateCreateForm = (field, value) => {
        setCreateForm(prev => ({ ...prev, [field]: value }));
    };

    const toggleUserSelection = (userId) => {
        setCreateForm(prev => ({
            ...prev,
            target_users: prev.target_users.includes(userId)
                ? prev.target_users.filter(id => id !== userId)
                : [...prev.target_users, userId]
        }));
    };

    // Template management functions (disabled for now)

    const createTemplate = async () => {
        setMessage('❌ Template management not yet implemented');
    };

    const updateTemplate = async () => {
        setMessage('❌ Template management not yet implemented');
    };

    const deleteTemplate = async () => {
        setMessage('❌ Template management not yet implemented');
    };

    const editTemplate = () => {
        setMessage('❌ Template management not yet implemented');
    };

    const updateTemplateForm = () => {
        // Placeholder function
    };

    // Email queue functions

    // Bulk operations functions
    const updateBulkForm = (field, value) => {
        setBulkForm(prev => ({ ...prev, [field]: value }));
    };

    const sendBulkNotification = async () => {
        if (!bulkForm.title.trim() || !bulkForm.message.trim()) {
            setMessage('❌ Please fill in both title and message');
            return;
        }

        setSendingBulk(true);
        setMessage('');

        try {
            const payload = {
                notification_type: bulkForm.notification_type,
                title: bulkForm.title,
                message: bulkForm.message,
                channels: bulkForm.channels,
                action_url: bulkForm.action_url || null,
                action_text: bulkForm.action_text || null,
                expires_at: bulkForm.expires_at ? new Date(bulkForm.expires_at).toISOString() : null,
                scheduled_at: bulkForm.scheduled_at ? new Date(bulkForm.scheduled_at).toISOString() : null,
                target_users: bulkForm.target_type === 'all_users' ? null : [],
                target_role: bulkForm.target_type === 'by_role' ? bulkForm.target_role : null,
                template_id: bulkForm.template_id || null
            };

            const response = await apiCall('/admin/notifications/send-bulk', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                setMessage(`✅ Bulk notification sent successfully! Sent to ${result.sent_count} users${result.failed_count > 0 ? `, ${result.failed_count} failed` : ''}`);
                
                // Reset form
                setBulkForm({
                    notification_type: 'system_announcement',
                    title: '',
                    message: '',
                    channels: ['in_app'],
                    target_type: 'all_users',
                    target_role: 'user',
                    template_id: '',
                    action_url: '',
                    action_text: '',
                    expires_at: '',
                    scheduled_at: ''
                });
                
                // Reload stats
                loadDashboardData();
            } else {
                const error = await response.text();
                setMessage(`❌ Failed to send bulk notification: ${error}`);
            }
        } catch (err) {
            setMessage(`❌ Error sending bulk notification: ${err.message}`);
        } finally {
            setSendingBulk(false);
        }
    };

    // Load data after all functions are defined
    useEffect(() => {
        if (isAdmin) {
            loadDashboardData();
            loadUsers();
            // loadTemplates();
            // loadEmailQueue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    if (!isAdmin) {
        return null; // Will redirect in useEffect
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 py-8">
                    <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-videbits-600"></div>
                        <p className="mt-2 text-gray-500">Loading notifications...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Link
                                to="/admin"
                                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                <span>Back to Admin</span>
                            </Link>
                            <div className="h-6 w-px bg-gray-300"></div>
                            <div className="flex items-center space-x-3">
                                <Bell className="w-6 h-6 text-videbits-600" />
                                <h1 className="text-xl font-semibold text-gray-900">Notifications Management</h1>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-2 bg-videbits-100 px-3 py-1 rounded-full">
                                <Shield className="w-4 h-4 text-videbits-600" />
                                <span className="text-sm text-videbits-700 font-medium">
                                    {user?.firstName} {user?.lastName}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-8">
                {message && (
                    <div className="mb-6 p-4 bg-videbits-50 border border-videbits-200 rounded-lg">
                        <p className="text-videbits-800">{message}</p>
                    </div>
                )}

                {/* Navigation Tabs */}
                <div className="mb-8">
                    <nav className="flex space-x-8">
                        {[
                            { id: 'overview', label: 'Overview', icon: Bell },
                            { id: 'create', label: 'Create Notification', icon: Plus },
                            { id: 'templates', label: 'Templates', icon: FileText },
                            { id: 'queue', label: 'Email Queue', icon: Mail },
                            { id: 'bulk', label: 'Bulk Operations', icon: Send }
                        ].map(({ id, label, icon: Icon }) => (
                            <button
                                key={id}
                                onClick={() => setActiveTab(id)}
                                className={`flex items-center space-x-2 px-4 py-2 border-b-2 transition-colors ${
                                    activeTab === id
                                        ? 'border-videbits-600 text-videbits-600'
                    : 'border-transparent text-videbits-500 hover:text-videbits-700 hover:border-videbits-300'
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span className="font-medium">{label}</span>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                {activeTab === 'overview' && (
                    <div className="space-y-8">
                        {/* Stats Grid */}
                        {stats ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[
                                    { label: 'Total Notifications', value: stats.total_notifications || 0, icon: Bell, color: 'purple' },
                                    { label: 'Unread Count', value: stats.unread_count || 0, icon: Mail, color: 'yellow' },
                                    { label: 'Email Templates', value: stats.template_count || 0, icon: FileText, color: 'green' },
                                    { label: 'Active Users', value: users.length || 0, icon: Users, color: 'purple' }
                                ].map(({ label, value, icon: Icon, color }) => (
                                    <div key={label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium text-gray-600">{label}</p>
                                                <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
                                            </div>
                                            <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                                                <Icon className={`w-6 h-6 text-${color}-600`} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <p className="text-gray-500">No statistics available. Make sure you're logged in as an admin.</p>
                            </div>
                        )}

                        {/* Quick Actions */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <button
                                    onClick={() => setActiveTab('create')}
                            className="group flex items-center p-4 rounded-lg border-2 border-dashed border-videbits-300 hover:border-videbits-500 hover:bg-videbits-50 transition-all duration-200"
                                >
                                    <div className="w-10 h-10 bg-videbits-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-videbits-200 transition-colors">
                                        <Plus className="w-5 h-5 text-videbits-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-gray-900 group-hover:text-videbits-900">
                                            Create Notification
                                        </h3>
                                        <p className="text-sm text-gray-600 group-hover:text-videbits-700">
                                            Send notifications to users
                                        </p>
                                    </div>
                                </button>
                                
                                <button
                                    onClick={() => setActiveTab('queue')}
                            className="group flex items-center p-4 rounded-lg border-2 border-dashed border-videbits-300 hover:border-videbits-500 hover:bg-videbits-50 transition-all duration-200"
                                >
                                    <div className="w-10 h-10 bg-videbits-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-videbits-200 transition-colors">
                                        <Mail className="w-5 h-5 text-videbits-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-gray-900 group-hover:text-videbits-900">
                                            Process Email Queue
                                        </h3>
                                        <p className="text-sm text-gray-600 group-hover:text-videbits-700">
                                            Manage email delivery queue
                                        </p>
                                    </div>
                                </button>
                                
                                <div className="group flex items-center p-4 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50">
                                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                                        <FileText className="w-5 h-5 text-gray-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-gray-500">
                                            Template Management
                                        </h3>
                                        <p className="text-sm text-gray-400">
                                            Coming soon - Email templates
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'create' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Notification Form */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Details</h3>
                                    
                                    {/* Type Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                                        <select
                                            value={createForm.notification_type}
                                            onChange={(e) => updateCreateForm('notification_type', e.target.value)}
                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                        >
                                            <option value="system_announcement">System Announcement</option>
                                            <option value="welcome">Welcome</option>
                                            <option value="new_content">New Content</option>
                                            <option value="account_update">Account Update</option>
                                            <option value="reminder">Reminder</option>
                                            <option value="promotion">Promotion</option>
                                        </select>
                                    </div>

                                    {/* Title */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                        <input
                                            type="text"
                                            value={createForm.title}
                                            onChange={(e) => updateCreateForm('title', e.target.value)}
                                            placeholder="Enter notification title"
                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                        />
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                                        <textarea
                                            value={createForm.message}
                                            onChange={(e) => updateCreateForm('message', e.target.value)}
                                            placeholder="Enter notification message"
                                            rows="4"
                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                        />
                                    </div>

                                    {/* Action URL and Text */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Action URL (optional)</label>
                                            <input
                                                type="url"
                                                value={createForm.action_url}
                                                onChange={(e) => updateCreateForm('action_url', e.target.value)}
                                                placeholder="https://example.com"
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Action Text (optional)</label>
                                            <input
                                                type="text"
                                                value={createForm.action_text}
                                                onChange={(e) => updateCreateForm('action_text', e.target.value)}
                                                placeholder="View Details"
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>

                                    {/* Expiration Date */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Expires At (optional)</label>
                                        <input
                                            type="datetime-local"
                                            value={createForm.expires_at}
                                            onChange={(e) => updateCreateForm('expires_at', e.target.value)}
                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                        />
                                    </div>

                                    {/* Send Button */}
                                    <div className="pt-4">
                                        <button
                                            onClick={createNotification}
                                            disabled={sendingNotification}
                                            className="w-full bg-videbits-600 hover:bg-videbits-700 disabled:bg-videbits-800 text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                                        >
                                            {sendingNotification ? (
                                                <span>Sending...</span>
                                            ) : (
                                                <span>Send Notification</span>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* User Selection */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Target Users</h3>
                                    
                                    <div className="mb-4">
                                        <label className="flex items-center space-x-2">
                                            <input
                                                type="checkbox"
                                                checked={createForm.target_users.length === 0}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        updateCreateForm('target_users', []);
                                                    }
                                                }}
                                                className="form-checkbox h-4 w-4 text-videbits-600"
                                            />
                                            <span className="text-sm text-gray-700">Send to all users</span>
                                        </label>
                                    </div>

                                    {users.length > 0 && (
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                                            <p className="text-sm text-gray-600 mb-3">
                                                Select specific users (leave empty to send to all):
                                            </p>
                                            <div className="space-y-2">
                                                {users.map(user => (
                                                    <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-2 rounded">
                                                        <input
                                                            type="checkbox"
                                                            checked={createForm.target_users.includes(user.id)}
                                                            onChange={() => toggleUserSelection(user.id)}
                                                            className="form-checkbox h-4 w-4 text-videbits-600"
                                                        />
                                                        <div className="flex-1">
                                                            <span className="text-sm font-medium text-gray-900">{user.username}</span>
                                                            <span className="text-xs text-gray-500 ml-2">({user.email})</span>
                                                            {user.role === 'admin' && (
                                                                <span className="text-xs bg-videbits-600 text-white px-2 py-1 rounded ml-2">Admin</span>
                                                            )}
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Preview */}
                                    {(createForm.title || createForm.message) && (
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Preview:</h4>
                                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                                                {createForm.title && (
                                                    <h5 className="font-semibold text-gray-900 mb-1">{createForm.title}</h5>
                                                )}
                                                {createForm.message && (
                                                    <p className="text-gray-700 text-sm mb-2">{createForm.message}</p>
                                                )}
                                                {createForm.action_text && createForm.action_url && (
                                                    <button className="text-xs bg-videbits-600 hover:bg-videbits-700 text-white px-3 py-1 rounded transition-colors">
                                                        {createForm.action_text}
                                                    </button>
                                                )}
                                                <div className="text-xs text-gray-500 mt-2">
                                                    Type: {createForm.notification_type.replace('_', ' ')}
                                                    {createForm.expires_at && (
                                                        <span> • Expires: {new Date(createForm.expires_at).toLocaleString()}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'templates' && (
                    <div className="space-y-6">
                        {/* Templates Header */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <FileText className="w-5 h-5 text-gray-500" />
                                    <h2 className="text-lg font-semibold text-gray-900">Email Templates</h2>
                                </div>
                                <button
                                    onClick={() => setMessage('❌ Template management not yet implemented')}
                                    disabled
                                    className="flex items-center space-x-2 bg-gray-400 cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium"
                                >
                                    <Plus className="w-4 h-4" />
                                    <span>Create Template (Coming Soon)</span>
                                </button>
                            </div>

                            {/* Templates List */}
                            {templates.length > 0 ? (
                                <div className="space-y-4">
                                    {templates.map((template) => (
                                        <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-2 mb-2">
                                                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                                            template.is_active 
                                                                ? 'bg-green-100 text-green-800' 
                                                                : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {template.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-600 mb-1">
                                                        Type: {template.template_type.replace('_', ' ')}
                                                    </p>
                                                    <p className="text-sm text-gray-500">
                                                        Used {template.usage_count || 0} times
                                                    </p>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => editTemplate(template)}
                                                        className="p-2 text-videbits-500 hover:text-videbits-700 transition-colors"
                                                        title="Edit template"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteTemplate(template.id)}
                                                        className="p-2 text-videbits-500 hover:text-red-600 transition-colors"
                                                        title="Delete template"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-500 mb-2">No email templates found</p>
                                    <p className="text-sm text-gray-400">Create your first template to get started</p>
                                </div>
                            )}
                        </div>

                        {/* Template Modal - Coming Soon */}
                        <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="text-center py-8">
                                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 mb-2">Template Management Coming Soon</p>
                                <p className="text-sm text-gray-400">Advanced template features will be available in a future update</p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'queue' && (
                    <div className="space-y-6">
                        {/* Queue Header */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center space-x-3">
                                    <Mail className="w-5 h-5 text-gray-500" />
                                    <h2 className="text-lg font-semibold text-gray-900">Email Queue Management</h2>
                                </div>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => console.log('Email queue refresh disabled')}
                                        className="flex items-center space-x-2 bg-videbits-600 hover:bg-videbits-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                                    >
                                        <Clock className="w-4 h-4" />
                                        <span>Refresh</span>
                                    </button>
                                    <button
                                        onClick={processEmailQueue}
                                        disabled={processingQueue}
                                        className="flex items-center space-x-2 bg-videbits-600 hover:bg-videbits-700 disabled:bg-videbits-800 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                                    >
                                        <Send className="w-4 h-4" />
                                        <span>{processingQueue ? 'Processing...' : 'Process Queue'}</span>
                                    </button>
                                </div>
                            </div>

                            {/* Queue Statistics */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-videbits-50 border border-videbits-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-videbits-800">Total Queued</p>
                                            <p className="text-2xl font-bold text-videbits-900">{emailQueue.length}</p>
                                        </div>
                                        <Clock className="w-8 h-8 text-videbits-600" />
                                    </div>
                                </div>
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-yellow-800">Pending</p>
                                            <p className="text-2xl font-bold text-yellow-900">
                                                {emailQueue.filter(item => item.status === 'pending').length}
                                            </p>
                                        </div>
                                        <Mail className="w-8 h-8 text-yellow-600" />
                                    </div>
                                </div>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-red-800">Failed</p>
                                            <p className="text-2xl font-bold text-red-900">
                                                {emailQueue.filter(item => item.status === 'failed').length}
                                            </p>
                                        </div>
                                        <XCircle className="w-8 h-8 text-red-600" />
                                    </div>
                                </div>
                            </div>

                            {/* Queue Items */}
                            {emailQueue.length > 0 ? (
                                <div className="space-y-3">
                                    <h3 className="text-md font-semibold text-gray-900">Queue Items</h3>
                                    <div className="space-y-2">
                                        {emailQueue.map((item) => (
                                            <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center space-x-3 mb-2">
                                                            <h4 className="font-medium text-gray-900">{item.subject}</h4>
                                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                                item.status === 'sent' 
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : item.status === 'failed'
                                                                    ? 'bg-red-100 text-red-800'
                                                                    : item.status === 'processing'
                                                                    ? 'bg-videbits-100 text-videbits-800'
                                                                    : 'bg-yellow-100 text-yellow-800'
                                                            }`}>
                                                                {item.status === 'sent' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                                                                {item.status === 'failed' && <XCircle className="w-3 h-3 inline mr-1" />}
                                                                {item.status === 'processing' && <Clock className="w-3 h-3 inline mr-1" />}
                                                                {item.status === 'pending' && <Clock className="w-3 h-3 inline mr-1" />}
                                                                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                                            </span>
                                                        </div>
                                                        <div className="text-sm text-gray-600 space-y-1">
                                                            <p><span className="font-medium">To:</span> {item.recipient_email}</p>
                                                            <p><span className="font-medium">Type:</span> {item.notification_type?.replace('_', ' ')}</p>
                                                            <p><span className="font-medium">Created:</span> {new Date(item.created_at).toLocaleString()}</p>
                                                            {item.sent_at && (
                                                                <p><span className="font-medium">Sent:</span> {new Date(item.sent_at).toLocaleString()}</p>
                                                            )}
                                                            {item.error_message && (
                                                                <p className="text-red-600"><span className="font-medium">Error:</span> {item.error_message}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        {item.status === 'sent' && <CheckCircle className="w-5 h-5 text-green-600" />}
                                                        {item.status === 'failed' && <XCircle className="w-5 h-5 text-red-600" />}
                                                        {(item.status === 'pending' || item.status === 'processing') && <Clock className="w-5 h-5 text-yellow-600" />}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-500 mb-2">No emails in queue</p>
                                    <p className="text-sm text-gray-400">Email queue is empty</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'bulk' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center space-x-3 mb-6">
                                <Send className="w-5 h-5 text-gray-500" />
                                <h2 className="text-lg font-semibold text-gray-900">Bulk Notifications</h2>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Bulk Notification Form */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Notification Details</h3>
                                    
                                    {/* Template Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Use Template (optional)</label>
                                        <select
                                            value={bulkForm.template_id}
                                            onChange={(e) => updateBulkForm('template_id', e.target.value)}
                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                        >
                                            <option value="">Custom notification</option>
                                            {templates.filter(t => t.is_active).map(template => (
                                                <option key={template.id} value={template.id}>
                                                    {template.name} ({template.template_type})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Type Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Notification Type</label>
                                        <select
                                            value={bulkForm.notification_type}
                                            onChange={(e) => updateBulkForm('notification_type', e.target.value)}
                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                        >
                                            <option value="system_announcement">System Announcement</option>
                                            <option value="welcome">Welcome</option>
                                            <option value="new_content">New Content</option>
                                            <option value="account_update">Account Update</option>
                                            <option value="reminder">Reminder</option>
                                            <option value="promotion">Promotion</option>
                                        </select>
                                    </div>

                                    {/* Title */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                        <input
                                            type="text"
                                            value={bulkForm.title}
                                            onChange={(e) => updateBulkForm('title', e.target.value)}
                                            placeholder="Enter notification title"
                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                        />
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                                        <textarea
                                            value={bulkForm.message}
                                            onChange={(e) => updateBulkForm('message', e.target.value)}
                                            placeholder="Enter notification message"
                                            rows="4"
                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                        />
                                    </div>

                                    {/* Action URL and Text */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Action URL (optional)</label>
                                            <input
                                                type="url"
                                                value={bulkForm.action_url}
                                                onChange={(e) => updateBulkForm('action_url', e.target.value)}
                                                placeholder="https://example.com"
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Action Text (optional)</label>
                                            <input
                                                type="text"
                                                value={bulkForm.action_text}
                                                onChange={(e) => updateBulkForm('action_text', e.target.value)}
                                                placeholder="View Details"
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>

                                    {/* Scheduling */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Schedule For (optional)</label>
                                            <input
                                                type="datetime-local"
                                                value={bulkForm.scheduled_at}
                                                onChange={(e) => updateBulkForm('scheduled_at', e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Expires At (optional)</label>
                                            <input
                                                type="datetime-local"
                                                value={bulkForm.expires_at}
                                                onChange={(e) => updateBulkForm('expires_at', e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>

                                    {/* Send Button */}
                                    <div className="pt-4">
                                        <button
                                            onClick={sendBulkNotification}
                                            disabled={sendingBulk}
                                            className="w-full bg-videbits-600 hover:bg-videbits-700 disabled:bg-videbits-800 text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                                        >
                                            <Send className="w-4 h-4 mr-2" />
                                            {sendingBulk ? (
                                                <span>Sending Bulk Notification...</span>
                                            ) : (
                                                <span>Send Bulk Notification</span>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Target Selection and Preview */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Target Audience</h3>
                                    
                                    {/* Target Type Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Target Type</label>
                                        <select
                                            value={bulkForm.target_type}
                                            onChange={(e) => updateBulkForm('target_type', e.target.value)}
                                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                        >
                                            <option value="all_users">All Users</option>
                                            <option value="by_role">By User Role</option>
                                            <option value="active_users">Active Users Only</option>
                                        </select>
                                    </div>

                                    {/* Role Selection (if by_role is selected) */}
                                    {bulkForm.target_type === 'by_role' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Target Role</label>
                                            <select
                                                value={bulkForm.target_role}
                                                onChange={(e) => updateBulkForm('target_role', e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-transparent"
                                            >
                                                <option value="user">Regular Users</option>
                                                <option value="admin">Administrators</option>
                                            </select>
                                        </div>
                                    )}

                                    {/* Channel Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Notification Channels</label>
                                        <div className="space-y-2">
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    checked={bulkForm.channels.includes('in_app')}
                                                    onChange={(e) => {
                                                        const channels = e.target.checked
                                                            ? [...bulkForm.channels, 'in_app']
                                                            : bulkForm.channels.filter(c => c !== 'in_app');
                                                        updateBulkForm('channels', channels);
                                                    }}
                                                    className="form-checkbox h-4 w-4 text-videbits-600"
                                                />
                                                <span className="text-sm text-gray-700">In-App Notification</span>
                                            </label>
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    checked={bulkForm.channels.includes('email')}
                                                    onChange={(e) => {
                                                        const channels = e.target.checked
                                                            ? [...bulkForm.channels, 'email']
                                                            : bulkForm.channels.filter(c => c !== 'email');
                                                        updateBulkForm('channels', channels);
                                                    }}
                                                    className="form-checkbox h-4 w-4 text-videbits-600"
                                                />
                                                <span className="text-sm text-gray-700">Email Notification</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Target Summary */}
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">Target Summary:</h4>
                                        <div className="text-sm text-gray-600 space-y-1">
                                            <p><span className="font-medium">Target:</span> {
                                                bulkForm.target_type === 'all_users' ? 'All users' :
                                                bulkForm.target_type === 'by_role' ? `${bulkForm.target_role === 'admin' ? 'Administrators' : 'Regular users'} only` :
                                                'Active users only'
                                            }</p>
                                            <p><span className="font-medium">Channels:</span> {bulkForm.channels.join(', ')}</p>
                                            <p><span className="font-medium">Estimated Recipients:</span> {
                                                bulkForm.target_type === 'all_users' ? users.length :
                                                bulkForm.target_type === 'by_role' ? users.filter(u => u.role === bulkForm.target_role).length :
                                                users.length
                                            } users</p>
                                            {bulkForm.scheduled_at && (
                                                <p><span className="font-medium">Scheduled:</span> {new Date(bulkForm.scheduled_at).toLocaleString()}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Preview */}
                                    {(bulkForm.title || bulkForm.message) && (
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Preview:</h4>
                                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                                                {bulkForm.title && (
                                                    <h5 className="font-semibold text-gray-900 mb-1">{bulkForm.title}</h5>
                                                )}
                                                {bulkForm.message && (
                                                    <p className="text-gray-700 text-sm mb-2">{bulkForm.message}</p>
                                                )}
                                                {bulkForm.action_text && bulkForm.action_url && (
                                                    <button className="text-xs bg-videbits-600 hover:bg-videbits-700 text-white px-3 py-1 rounded transition-colors">
                                                        {bulkForm.action_text}
                                                    </button>
                                                )}
                                                <div className="text-xs text-gray-500 mt-2">
                                                    Type: {bulkForm.notification_type.replace('_', ' ')}
                                                    • Channels: {bulkForm.channels.join(', ')}
                                                    {bulkForm.expires_at && (
                                                        <span> • Expires: {new Date(bulkForm.expires_at).toLocaleString()}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Admin Info */}
                <div className="mt-8 text-center text-gray-500 text-sm">
                    Logged in as: {user?.username} ({user?.role})
                </div>
            </div>
        </div>
    );
};

export default AdminNotifications;
